import { useEffect, useState, type ReactNode } from 'react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import superjson from 'superjson';
import type { AppRouter } from '@servwell/api/router';
import { TRPCProvider } from './trpc';
import { pendingWrites, queryClient } from './query';
import { writeGuardLink } from './write-guard-link';
import { tapAgeLink } from './tap-age-link';
import { registerReplay, trackOfflineWrites } from './offline-replay';
import { clearLandedDraft } from './useDraft';
import { startUploadQueue, uploadQueue } from './upload-queue';
import { startTicketPhotos } from './ticket-photos';
import { failedWriteLog, startFailedWriteLog } from './failed-writes';
import { identity, persistOptions } from './identity';
import type { QueryClient } from '@tanstack/react-query';
import type { PendingWrites } from './pending-writes';
import { sameAccount, type AccountRef } from './account';
import { keyOf } from './idempotency';
import { restockSendInvalidatesQuery } from './restock-query-policy';

/** Success is already accepted. Clearance/refetch failures must never turn it into a resend. */
export function createReplayLanding(
  opts: {
    queryClient?: QueryClient;
    writes?: PendingWrites;
    account?: () => AccountRef | null;
    clearDraft?: typeof clearLandedDraft;
  } = {},
) {
  const client = opts.queryClient ?? queryClient,
    writes = opts.writes ?? pendingWrites;
  const account =
    opts.account ??
    (() => {
      const snapshot = identity.cacheAccount.snapshot();
      return snapshot.status === 'settled' ? snapshot.account : null;
    });
  const clearDraft = opts.clearDraft ?? clearLandedDraft;
  return (path: string, variables: unknown, meta: unknown): void => {
    // Capture THIS successful attempt while TanStack still marks it pending.
    // Older failed/successful attempts may retain the same original delivery key.
    const key = keyOf(variables);
    const matches =
      path === 'restocks.check' && key
        ? client
            .getMutationCache()
            .getAll()
            .filter((m) => {
              const parts = m.options.mutationKey?.[0],
                storedKey = keyOf(m.state.variables);
              return (
                m.state.status === 'pending' &&
                Array.isArray(parts) &&
                parts.join('.') === path &&
                storedKey?.clientId === key.clientId &&
                storedKey?.mutationId === key.mutationId
              );
            })
        : [];
    const attempt = matches.length === 1 ? matches[0] : undefined;
    void (async () => {
      await clearDraft(path, variables, meta);
      if (!attempt || !client.getMutationCache().getAll().includes(attempt)) return;
      // A switched/cleared cache, two possible deliveries, or an unknown stamp is not
      // authority to refresh the current account. Never use response data to patch it.
      const owner = writes.inspect.madeBy(attempt),
        current = account();
      if (!sameAccount(owner, current)) return;
      await client.invalidateQueries({
        predicate: (query) => {
          const parts = query.queryKey[0];
          if (!Array.isArray(parts)) return false;
          return restockSendInvalidatesQuery(parts);
        },
      });
    })().catch(() => {});
  };
}

/** Once the saved cache is restored (or failed to be): bring the queued writes back. */
function afterRestore(): void {
  pendingWrites.start();
  void failedWriteLog.reload().catch(() => {});
}

/** The app's tRPC client: every request through the write guard, then to `/trpc`. */
export function createAppTrpcClient(send: typeof fetch = (...args) => fetch(...args)) {
  return createTRPCClient<AppRouter>({
    links: [
      // Every mutation passes the write guard first: nothing is sent unless this tab writes
      // as the account that made it (MAP 225b). See write-guard-link.ts.
      writeGuardLink(pendingWrites),
      // A timer tap goes out as how long ago it was, by the phone's own clock (062).
      tapAgeLink(),
      httpBatchLink({
        url: '/trpc', // same-origin (Vite proxy in dev; the API serves the SPA in prod)
        transformer: superjson, // v11: the transformer lives on the LINK
        fetch: (url, opts) => send(url, { ...opts, credentials: 'include' }),
      }),
    ],
  });
}

export function ApiProvider({ children }: { children: ReactNode }) {
  const [trpcClient] = useState(() => createAppTrpcClient());

  // Restored mutations are rebuilt from their key alone, so the function has to be
  // findable by key. Registered before the provider mounts and hydrates. See offline-replay.
  // A note the queue delivers clears the draft it was typed in, as the screen would have.
  useState(() =>
    registerReplay(queryClient, trpcClient, {
      onLanded: createReplayLanding(),
    }),
  );

  // Recorded from the first render, NOT in an effect: the persister hydrates restored
  // mutations after this component mounts, and a restored write arrives already paused. An
  // effect would run too late to see it, and it would be invisible to the banner for the
  // rest of the session — the exact silence this ticket is about.
  useState(() => trackOfflineWrites(queryClient));

  // Refused work from previous sessions, read back as early as possible so the panel can
  // show it. A refusal no longer needs a recorder watching the cache: every replayable write
  // hands its own final refusal to the log, under the account that made it, from the cache's `onError`
  // (MAP 225b, pending-writes.ts). See failed-writes.ts.
  useState(() => startFailedWriteLog());

  // Photos queue in a store of their own and therefore need their own driver — they are not
  // mutations, so `resumePausedMutations` and the retryer's online subscription know nothing
  // about them. See upload-queue.ts for why they cannot be mutations.
  // A ticket's photos follow its flag (ticket 03): the hold rule is set BEFORE the queue's
  // first drain below (effects run in order), so no photo goes ahead of its flag at a start.
  useEffect(
    () =>
      startTicketPhotos({
        queue: uploadQueue,
        queryClient,
        writes: pendingWrites,
        log: failedWriteLog,
      }),
    [],
  );
  useEffect(() => startUploadQueue(), []);

  // Other tabs: hear when one changes who is signed in (MAP 225a). See identity-tabs.ts.
  useEffect(() => identity.listenToOtherTabs(), []);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      // One set of options, shared with the gate's own re-persist: the saved cache carries
      // its account and never `me.session`, and only replayable writes are queued (MAP 225a;
      // see identity.ts, cache-account.ts and shouldDehydrateMutation).
      persistOptions={persistOptions}
      // THE line that was missing, in its MAP 225b form. `onlineManager.subscribe` fires on a
      // connectivity CHANGE; opening the app fresh while already online is not a change, so
      // without this a queued write restored from IndexedDB sat there forever. Queued writes
      // now come back from their own store, after the restore has moved any old ones into it,
      // and only the drawn account's are built and resumed (pending-writes.ts). The failed-
      // write log is read again too: the move may have given old rows with no account one.
      onSuccess={afterRestore}
      onError={afterRestore}
    >
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </PersistQueryClientProvider>
  );
}
